import CommonDataGrid from '@/components/common/CommonDataGrid';
import { useAppSelector } from '@/store/hooks';
import type { RootState } from '@/store/store';
import { Box, Button, Checkbox, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { usePermissions } from '../../../hooks/usePermissions';
import { StatusDot } from '@/components/common/styled-components';
import { useAuth0 } from '@auth0/auth0-react';
import DeviceService from '../../../services/devices';
import ConfirmationModal from '@/components/common/modals/ConfirmationModal';
import MessageModal from '@/components/common/modals/MessageModal';

interface TurnaroundDetailGridProps {
  turnaroundId: string | undefined;
  userId: string | null;
  filters: any;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  onBulkDelete: (deviceIds: string[]) => void;
}

const TurnaroundDetailGrid: React.FC<TurnaroundDetailGridProps> = ({
  turnaroundId,
  userId,
  filters,
  paginationModel,
  onPaginationModelChange,
  onBulkDelete,
}) => {
  const { t } = useTranslation();
  const { getFeatureActions } = usePermissions();
  const viewConditionPermissions = getFeatureActions('Devices', 'View Condition Column', 'Device List');
  const { getAccessTokenSilently } = useAuth0();

  // Get turnaround detail data from Redux store - no fetching here, just reading
  const turnaroundDetail = useAppSelector(
    (state: RootState) => turnaroundId ? state.turnaroundDetail.details[turnaroundId] : undefined
  );
  
  const turnaroundData = turnaroundDetail?.data || null;
  const loading = turnaroundDetail?.loading || false;
  const error = turnaroundDetail?.error || null;

  // Local UI state only
  const [rows, setRows] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  const [openPartsConfirmDialog, setOpenPartsConfirmDialog] = useState(false);
  const [showPartsSuccessDialog, setShowPartsSuccessDialog] = useState(false);
  const [partsDeviceIds, setPartsDeviceIds] = useState<string[]>([]);

  // Filter rows whenever turnaroundData or filters change
  useEffect(() => {
    if (!turnaroundData) {
      setRows([]);
      setTotalCount(0);
      setSelectedRowIds([]);
      return;
    }

    let devices = turnaroundData.devices || [];

    // Apply filters
    if (filters) {
      // Search filter
      if (filters.search) {
        const search = filters.search.toLowerCase();
        devices = devices.filter(device =>
          (device.serial_number && device.serial_number.toLowerCase().includes(search)) ||
          (device.tag_no && device.tag_no.toLowerCase().includes(search)) ||
          (device.device_type && device.device_type.toLowerCase().includes(search))
        );
      }

      // Device type filter
      if (filters.device_type && filters.device_type.length > 0) {
        devices = devices.filter(device =>
          device.device_type && filters.device_type.includes(device.device_type)
        );
      }

      // Device status filter
      if (filters.device_state) {
        const stateFilter = filters.device_state.toLowerCase();

        if (stateFilter === 'decommissioned') {
          devices = devices.filter(device => device.decommissioned);
        } else if (stateFilter === 'is_critical') {
          devices = devices.filter(device => device.is_critical);
        } else if (stateFilter === 'site_verified') {
          devices = devices.filter(device => device.site_verified);
        } else {
          devices = devices.filter(device =>
            device.lifecycle_status && device.lifecycle_status.toLowerCase().includes(stateFilter)
          );
        }
      }
    }

    const mappedRows = devices.map(device => ({
      id: device.device_id,
      tag: device.tag_no ?? '',
      serial_number: device.serial_number ?? '',
      device_type: device.device_type ?? '',
      brand: device.manufacturer ?? '',
      base_model: device.base_model ?? '',
      location: device.location_name ?? '',
      status: device.lifecycle_status ?? turnaroundData.status,
      critical: device.is_critical ? 'Yes' : 'No',
    }));

    setRows(mappedRows);
    setTotalCount(mappedRows.length);
    setSelectedRowIds([]); // Clear selection on filter change
  }, [filters, turnaroundData]);

  // Get current page row IDs for select-all-on-page
  const currentPageRowIds = useMemo(() => rows.map((row) => row.id), [rows]);
  const allSelectedOnPage = currentPageRowIds.length > 0 && currentPageRowIds.every(id => selectedRowIds.includes(String(id)));
  const someSelectedOnPage = currentPageRowIds.some(id => selectedRowIds.includes(String(id)));

  // Individual row checkbox
  const handleCheckboxChange = (rowId: string, checked: boolean) => {
    if (checked) {
      if (!selectedRowIds.includes(String(rowId))) {
        setSelectedRowIds([...selectedRowIds, String(rowId)]);
      }
    } else {
      setSelectedRowIds(selectedRowIds.filter((id) => id !== String(rowId)));
    }
  };

  // Header checkbox (select/deselect all on page)
  const handleHeaderCheckboxChange = (checked: boolean) => {
    if (!checked) {
      setSelectedRowIds(selectedRowIds.filter(id => !currentPageRowIds.map(String).includes(String(id))));
    } else {
      const prevSet = new Set(selectedRowIds.map(String));
      const result = [...selectedRowIds.map(String)];
      for (let i = 0; i < currentPageRowIds.length; i++) {
        const idStr = String(currentPageRowIds[i]);
        if (!prevSet.has(idStr)) {
          result.push(idStr);
        }
      }
      setSelectedRowIds(result);
    }
  };

  // Custom checkbox column with header checkbox
  const checkboxColumn: GridColDef = {
    field: "select",
    headerName: "",
    renderHeader: () => {
      const indeterminate = !allSelectedOnPage && someSelectedOnPage;
      return (
        <Checkbox
          checked={allSelectedOnPage}
          indeterminate={indeterminate}
          onChange={(_, checked) => handleHeaderCheckboxChange(checked)}
          color={indeterminate ? 'primary' : 'secondary'}
          size="small"
          slotProps={{
            input: {
              'aria-label': 'Select all rows on page',
            },
          }}
        />
      );
    },
    width: 80,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => (
      <Checkbox
        checked={selectedRowIds.includes(params.row.id)}
        onChange={(_, checked) => handleCheckboxChange(params.row.id, checked)}
        color="secondary"
        size="small"
        slotProps={{
          input: {
            'aria-label': `Select row ${params.row.id}`,
          },
        }}
      />
    ),
  };

  // Actions column state
  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null);
  const [actionsRowId, setActionsRowId] = useState<string | null>(null);

  const handleActionsClick = (event: React.MouseEvent<HTMLElement>, rowId: string) => {
    setActionsAnchorEl(event.currentTarget);
    setActionsRowId(rowId);
  };

  const handleActionsClose = () => {
    setActionsAnchorEl(null);
    setActionsRowId(null);
  };

  const handleDeleteClick = (rowId?: string) => {
    const idsToDelete = rowId ? [rowId] : selectedRowIds;
    onBulkDelete(idsToDelete);
    handleActionsClose();
  };


  const handleGetPartsClick = (rowId?: string) => {
    const deviceIds = rowId ? [rowId] : selectedRowIds;
    setPartsDeviceIds(deviceIds);
    setOpenPartsConfirmDialog(true);
    handleActionsClose();
  };

  const handleGetPartsAndAccessories = async () => {
    setOpenPartsConfirmDialog(false);
    setShowPartsSuccessDialog(true);

    const params = {
      user_id: userId,
      device_ids: partsDeviceIds,
      report_type: "turnaround",
      report_data: turnaroundData?.name || "",
    };

    try {
      const accessToken = await getAccessTokenSilently();
      const deviceService = new DeviceService(undefined, accessToken);
      await deviceService.generateEspirReport(params);
      console.log("Parts and accessories request submitted successfully");
    } catch (err) {
      console.error("Parts and accessories request failed:", err);
    }
  };

  const handleBulkGetPartsClick = () => {
    setPartsDeviceIds(selectedRowIds);
    setOpenPartsConfirmDialog(true);
  };

  const actionsColumn: GridColDef = {
    field: 'actions',
    headerName: t('actions', 'ACTIONS'),
    width: 90,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => (
      <>
        <IconButton
          size="small"
          onClick={(e) => handleActionsClick(e, params.row.id)}
          aria-label="actions"
        >
          &#8942;
        </IconButton>
        {actionsAnchorEl && actionsRowId === params.row.id && (
          <Menu
            anchorEl={actionsAnchorEl}
            open={Boolean(actionsAnchorEl)}
            onClose={handleActionsClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => handleDeleteClick(params.row.id)}>
              {t('deleteDevices', 'Delete Devices')} {selectedRowIds.length > 0 ? `(${selectedRowIds.length})` : ''}
            </MenuItem>
            <MenuItem onClick={() => handleGetPartsClick(params.row.id)}>
              {t('getPartsAndAccessories', 'Get Parts and Accessories')}
            </MenuItem>
          </Menu>
        )}
      </>
    ),
  };

  const columns = useMemo(() => {
    const baseColumns: GridColDef[] = [
      checkboxColumn,
      {
        field: "tag",
        headerName: t("tag", "TAG"),
        flex: 1,
        minWidth: 120,
        renderCell: (params) =>
          params.value ? (
            <Link
              to={`/devices/${params.row.id}`}
              className="turnaround-link font-weight-medium text-primary"
              style={{ textDecoration: 'none', color: 'var(--primary)' }}
            >
              {params.value}
            </Link>
          ) : "-",
      },
      {
        field: "serial_number",
        headerName: t("serialNumber", "SERIAL NUMBER"),
        flex: 1,
        minWidth: 160,
        renderCell: (params) =>
          params.value ? (
            <Link
              to={`/devices/${params.row.id}`}
              className="turnaround-link font-weight-medium text-primary"
              style={{ textDecoration: 'none', color: 'var(--primary)' }}
            >
              {params.value}
            </Link>
          ) : "-",
      },
      {
        field: "device_type",
        headerName: t("deviceType", "DEVICE TYPE"),
        flex: 1,
        minWidth: 120,
        renderCell: (params) => params.value || "-",
      },
      {
        field: "brand",
        headerName: t("searchResults_brand", "BRAND"),
        flex: 1,
        minWidth: 120,
        renderCell: (params) => params.value || "-",
      },
      {
        field: "base_model",
        headerName: t("searchResults_baseModel", "BASE MODEL"),
        flex: 1,
        minWidth: 120,
        renderCell: (params) => params.value || "-",
      },
      {
        field: "location",
        headerName: t("location", "LOCATION"),
        flex: 1,
        minWidth: 140,
        renderCell: (params) => params.value || "-",
      },
      {
        field: "status",
        headerName: t("status", "STATUS"),
        flex: 1,
        minWidth: 120,
        renderCell: (params) => {
          const status = params.value ? params.value.toLowerCase() : "";
          let color = '#eaeaea';
          switch (status) {
            case 'supported':
              color = '#FEC944';
              break;
            case 'retired':
              color = '#F79428';
              break;
            case 'obsolete':
              color = '#EC2257';
              break;
            case 'mature':
              color = '#014B8D';
              break;
            case 'current':
              color = '#88C7FF';
              break;
            case 'active':
              color = '#319FFF';
              break;
            default:
              color = '#CCCCCC';
          }
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', width: '100%' }}>
              <Tooltip title={params.value ? params.value : "-"} placement="top">
                <StatusDot
                  className='text-primary'
                  sx={{
                    backgroundColor: color,
                    width: 15,
                    height: 15,
                    borderRadius: '50%',
                    display: 'inline-block',
                  }}
                />
              </Tooltip>
            </Box>
          );
        },
      },
      {
        field: "critical",
        headerName: t("critical", "CRITICAL"),
        flex: 1,
        minWidth: 100,
        renderCell: (params) => params.value || "-",
      },
      actionsColumn,
    ];
    return baseColumns;
  }, [checkboxColumn, actionsColumn, t, viewConditionPermissions?.canView, selectedRowIds.length]);

  const handleBulkDeleteClick = () => {
    onBulkDelete(selectedRowIds);
  };

  const handleClearSelection = () => {
    setSelectedRowIds([]);
  };

  return (
    <>
      {selectedRowIds.length > 0 && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          mb: 2,
          backgroundColor: '#f5f5f5',
          borderRadius: 1,
          border: '1px solid #e0e0e0'
        }}>
          <Typography variant="body2">
            {selectedRowIds.length}{" "}
            {selectedRowIds.length > 1
              ? t("devicesSelectedCount", "devices")
              : t("deviceSelectedCount", "device")}{" "}
            {t("selected", "selected")}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleBulkGetPartsClick}
              data-testid="bulk-parts-button"
            >
              {t("getPartsAndAccessories", "Get Parts and Accessories")}
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              onClick={handleBulkDeleteClick}
              data-testid="bulk-delete-button"
            >
              {t("deleteDevices", "Remove Devices")}
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={handleClearSelection}
            >
              {t("clearSelection", "Clear Selection")}
            </Button>
          </Box>
        </Box>
      )}
      <CommonDataGrid
        columns={columns}
        rows={rows}
        totalRowCount={totalCount}
        loading={loading}
        pageName="Turnaround Details"
        error={error}
        paginationMode='client'
        hideHeaderCount={true}
        noRowsMessage={
          error ? error : t("noDevicesFound", "No devices found.")
        }
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[25, 50, 75, 100]}
        hideFooterEntries={true}
      />

      {/* Parts and Accessories Confirmation Dialog */}
      <ConfirmationModal
        open={openPartsConfirmDialog}
        onClose={() => setOpenPartsConfirmDialog(false)}
        onConfirm={handleGetPartsAndAccessories}
        title={t("getPartsAndAccessories", "Get Parts and Accessories")}
        message={t("partsAccessoriesConfirmMessage", "Parts and accessories will be fetched for the selected device(s). You will receive an email notification when the task is completed.")}
        confirmText={t("confirm", "Confirm")}
        cancelText={t("cancel", "Cancel")}
      />

      {/* Parts and Accessories Success Dialog */}
      <MessageModal
        open={showPartsSuccessDialog}
        onClose={() => setShowPartsSuccessDialog(false)}
        title={t("partsAccessoriesRequested", "Parts and Accessories Requested")}
        message={t("partsAccessoriesSuccessMessage", "Your request has been submitted successfully. Parts and accessories data will be processed, and you will receive an email notification once completed.")}
        buttonText={t("close", "Close")}
        onButtonClick={() => setShowPartsSuccessDialog(false)}
        showButton={true}
      />
    </>
  );
};

export default TurnaroundDetailGrid;
